import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { getViewParameterValue, requestYieldCallback } from '@theme/utilities';

/**
 * @typedef {import('./component').Refs & { fieldsets?: HTMLFieldSetElement[] }} VariantPickerRefs
 */

/**
 * A lightweight variant picker that keeps the product page, quick add modal, and
 * section-rendered product-card responses in sync.
 *
 * @extends {Component<VariantPickerRefs>}
 */
export default class VariantPicker extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<number[]>} */
  #checkedIndices = [];

  connectedCallback() {
    super.connectedCallback();
    this.#cacheFieldsets();
    this.addEventListener('change', this.variantChanged);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cacheFieldsets();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.variantChanged);
    this.#abortController?.abort();
  }

  get selectedOption() {
    return /** @type {HTMLElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-option-value-id], select option:checked[data-option-value-id]')
    );
  }

  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId ?? '';
  }

  get selectedOptionsValues() {
    /** @type {string[]} */
    const values = [];

    this.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked[data-option-value-id]');
      const optionValueId = checkedInput?.getAttribute('data-option-value-id');

      if (optionValueId) {
        values.push(optionValueId);
      }
    });

    this.querySelectorAll('select').forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      const optionValueId = selectedOption?.dataset.optionValueId;

      if (optionValueId) {
        values.push(optionValueId);
      }
    });

    return values;
  }

  #cacheFieldsets() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = fieldsets.map((fieldset) => [...fieldset.querySelectorAll('input[type="radio"][data-option-value-id]')]);
    this.#checkedIndices = this.#radios.map((radios) => {
      const checkedIndices = [];
      const currentIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true' || radio.checked);
      const previousIndex = radios.findIndex((radio) => radio.dataset.previousChecked === 'true');

      if (currentIndex >= 0) checkedIndices.push(currentIndex);
      if (previousIndex >= 0) checkedIndices.push(previousIndex);

      return checkedIndices;
    });
  }

  /**
   * Handles a variant selection change.
   * @param {Event} event
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!(selectedOption instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);
  };

  /**
   * Updates the selected option UI state.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) {
        throw new Error('Target element not found');
      }

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '', 10);
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '', 10);

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }

          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          radios.forEach((radio) => {
            radio.dataset.currentChecked = 'false';
          });

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }

      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const selectedValue = target.value;

      Array.from(target.options).forEach((option) => {
        option.removeAttribute('selected');
      });

      const newSelectedOption = Array.from(target.options).find((option) => option.value === selectedValue);

      if (!newSelectedOption) {
        throw new Error('Option not found');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL for the newly selected variant combination.
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl || '';
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${encodeURIComponent(viewParamValue)}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.map((value) => encodeURIComponent(value)).join(',')}`);
    } else if (source === 'product-card') {
      const sourceValues = sourceSelectedOptionsValues.length
        ? sourceSelectedOptionsValues
        : [selectedOption.dataset.optionValueId || ''].filter(Boolean);

      if (sourceValues.length) {
        params.push(`option_values=${sourceValues.map((value) => encodeURIComponent(value)).join(',')}`);
      }
    }

    if (productUrl.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }

    if (
      this.closest('product-card') ||
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      params.unshift('section_id=section-rendering-product-card');
    }

    return params.length ? `${productUrl}?${params.join('&')}` : productUrl;
  }

  /**
   * Fetches updated markup and dispatches a variant update event.
   * @param {string} requestUrl
   * @param {boolean} [shouldMorphMain]
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const nextPicker =
          html.querySelector(`${this.tagName.toLowerCase()}[data-product-id="${this.dataset.productId}"]`) ||
          html.querySelector(this.tagName.toLowerCase());

        const textContent =
          nextPicker?.querySelector('script[type="application/json"]')?.textContent ||
          html.querySelector('variant-picker script[type="application/json"]')?.textContent;

        if (!textContent) return;

        const resource = JSON.parse(textContent);
        this.#syncBrowserUrl(resource?.id?.toString() ?? null, nextPicker?.dataset.productUrl ?? null);

        if (shouldMorphMain) {
          this.updateMain(html);
          return;
        }

        const newProduct = this.updateVariantPicker(html);

        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(resource, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        console.error(error);
      });
  }

  /**
   * @typedef {{ id: string, url: string }} NewProduct
   */

  /**
   * Updates just the picker markup from a fresh HTML response.
   * @param {Document} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource =
      newHtml.querySelector(`${this.tagName.toLowerCase()}[data-product-id="${this.dataset.productId}"]`) ||
      newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      if (newProductId) {
        this.dataset.productId = newProductId;
      }

      if (newProductUrl) {
        this.dataset.productUrl = newProductUrl;
      }
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Updates the main product content when the selected option points to a connected product.
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const currentGrid = this.closest('[data-product-grid-content]');
    const newGrid = newHtml.querySelector('[data-product-grid-content]');

    if (currentGrid && newGrid) {
      morph(currentGrid, newGrid);
      return;
    }

    const currentSection = this.closest('.shopify-section');
    const newSection = currentSection?.id ? newHtml.getElementById(currentSection.id) : null;

    if (currentSection && newSection) {
      morph(currentSection, newSection);
      return;
    }

    this.updateVariantPicker(newHtml);
  }

  #syncBrowserUrl(variantId, nextProductUrl = null) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' && !this.closest('product-card') && !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const url = new URL(window.location.href);

    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (nextProductUrl) {
      url.pathname = nextProductUrl.split('?')[0];
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
